import React, { Fragment } from "react"
import PropTypes from "prop-types"
import getValue from "object-getvalue"

// Config
import PRConfig from "components/Stacker/config/ProductConfiguratorConfig"
import FRMC from "components/Stacker/config/FormConfig"

import CCCC from "components/Stacker/constants/ContactCustomerCareConstants"

// Utils
import { getS7ConfigUrl } from "lib/utils/productConfig-util"

// Actions
import { getProductDetails } from "components/Stacker/actions/ProductDetailActions"

// Components
import ProductOverview from "components/Stacker/components/ProductOverview/ProductOverview"
import ProductName from "components/Stacker/components/ProductName/ProductName"

// Styles
import "./Step4Confirm.scss"
import { getDY_SR } from "stackerConfig"

import { useEffect } from "react"
import { useRef } from "react"
import { useDispatch } from "react-redux"
import { useContext } from "react"
import { StackerContext } from "components/DYStackers/StackerContext"
import { useSelector } from "react-redux"
import { useI18nContext } from "i18n/i18n-react"

function Step4Confirm({
  containerNode,
  setContainerHeight,
  metal,
  setScroll,
  configProductSet,
  font,
  configTypeValue,
  text,
  design,
  icon,
  motifCode,
  fontCode,
  theme,
  isQuickView,
}) {
  const node = useRef()
  const { LL } = useI18nContext()
  const dispatch = useDispatch()
  const stackerContext = useContext(StackerContext)
  const {
    pid,
    quickViewPDPUrl,
    pdp,
    pdp: {
      data: {
        products,
        products: { collection, categoryPath, isEngagementProduct },
        sizeChart,
        metals,
      },
      selectedVariant,
      selectedVariant: { productName, isAffirmEnabled },
      selectedVariantIndex,
      priceInventory,
      priceInventory: { price: { formattedValue, discountedPrice } = {} } = {},
      selectedSize,
      sizeSelectionError,
      isGiftCard,
      selectedAmount,
    },
    swatch,
    swatchDropDown,
    baseStore,
    cartMetaData,
    enagraveableProductConfig,
    messageCharCount,
    isPriceDisclaimerEnabled,
    isAffirmEnabledOnSite,
    isCsAgent,
  } = useSelector((state) => {
    const pid = getDY_SR().data.pid || state.pdp.currentQuickViewSelection.pid
    const messageCharCount =
      CCCC.MAX_MESSAGE_CHARS_ALLOWED -
      (getValue(
        state,
        `form.${FRMC.FORM_CONTACT_CUSTOMER_CARE}.values.message`,
        ""
      ).length || 0)

    return {
      pid,
      quickViewPDPUrl: state.pdp.currentQuickViewSelection.url,
      pdp: getValue(state, `pdp.${pid}`, {}),
      swatch: state.swatch,
      swatchDropDown: state.toggle.SWATCH_DROPDOWN_TOGGLE,
      baseStore: state.pdp.queryParams.baseStore,
      cartMetaData: state.cart.meta,
      enagraveableProductConfig: state.toggle.enagraveableProductConfig,
      messageCharCount,
      isPriceDisclaimerEnabled: state.application.isPriceDisclaimerEnabled,
      isAffirmEnabledOnSite: state.application.isAffirmEnabledOnSite,
      isCsAgent: false,
    }
  })

  useEffect(() => {
    const updateHeight = () => {
      const computedHeight = getComputedStyle(node.current).height
      setContainerHeight(computedHeight)
    }

    setContainerHeight(getComputedStyle(node.current).height)
    window.addEventListener("resize", updateHeight)

    setTimeout(() => {
      containerNode.style.overflow = "visible"
    }, 1000)

    setScroll(containerNode)

    const selectedMetalProduct = configProductSet.filter(
      (product) => metal === product.METAL_VALUE
    )
    const pid = selectedMetalProduct[0].PRODUCT_ID

    const selectedSwatch = Object.keys(swatch).length > 0 ? swatch[pid] : {}

    dispatch(
      getProductDetails({
        args: {
          productId: pid,
          baseStore,
          selectedSwatch,
        },
        retailerId: stackerContext.retailerId,
      })
    )
    return () => {
      if (containerNode) {
        containerNode.style.overflow = "hidden"
      }
      window.removeEventListener("resize", updateHeight)
    }
  }, [containerNode])

  useEffect(() => {
    const selectedSwatch = {}
    dispatch(
      getProductDetails({
        args: {
          productId: pid,
          baseStore,
          selectedSwatch,
        },
        retailerId: stackerContext.retailerId,
      })
    )
  }, [pid])

  const displayAffirm = isAffirmEnabledOnSite && isAffirmEnabled
  const imgUrl = getS7ConfigUrl(configTypeValue, theme, metal)

  return (
    <div className="pinky-wrap" ref={node}>
      <div className="container">
        <div
          className={`config-step-content config-${metal}-step3 config-step-content__${design.toLowerCase()}`}
        >
          <h2
            id="step-title"
            className="text-center config-step-content__step-title"
          >
            {configTypeValue === PRConfig.PENDANT_PRODUCTS.PRODUCT_TYPE ? (
              <Fragment>
                <p className="p-0 m-0">
                  {LL.productConfig.reviewPt1HeaderText()}
                </p>
                <p className="p-0 m-0">
                  {LL.productConfig.reviewPt2HeaderText()}
                </p>
              </Fragment>
            ) : (
              LL.productConfig.reviewHeaderText()
            )}
          </h2>

          <div className="app-js__product-config">
            <div className="row d-flex align-items-end">
              {text && design === PRConfig.DESIGN_VALUES[0] && (
                <div className="col-12 col-xl-8 product-config-container">
                  <div className="step-img-container">
                    <img
                      src={`${imgUrl}?$productConfigMonogram_desktop$`}
                      className="img-fluid"
                      alt=""
                    />
                  </div>
                  <div
                    className={`pinky-viewer letter-space-${
                      text ? text.length : "0"
                    } font-${font.toLowerCase()} ${metal}-config  d-flex align-items-center justify-content-center`}
                  >
                    <div className="pinky-text d-flex align-items-center justify-content-center">
                      <span className="letter-box first-letter">
                        {text.charAt(0)}
                      </span>
                      <span className="letter-box second-letter">
                        {text.charAt(1)}
                      </span>
                      <span className="letter-box third-letter">
                        {text.charAt(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {icon && design === PRConfig.DESIGN_VALUES[1] && (
                <div className="col-12 col-xl-8 product-config-container">
                  <div className="step-img-container">
                    <img
                      src={`${imgUrl}${icon.toLowerCase()}?$productConfigPDP_desktop$`}
                      className="img-fluid"
                      alt=""
                    />
                  </div>
                </div>
              )}
              <div className="col-12 col-xl-4 config-module-container d-flex align-self-center ">
                <div className="w-100">
                  <ProductName
                    collection={collection}
                    categoryPath={categoryPath}
                    productName={productName}
                    isEngagementProduct={isEngagementProduct}
                    isGiftCard={isGiftCard}
                    formattedValue={formattedValue}
                    discountedPrice={discountedPrice}
                    isPriceDisclaimerEnabled={isPriceDisclaimerEnabled}
                    displayAffirm={displayAffirm}
                    productTheme={theme}
                    isCsAgent={isCsAgent}
                  />
                  <ProductOverview
                    productDetails={products}
                    pid={pid}
                    selectedVariant={selectedVariant}
                    selectedVariantIndex={selectedVariantIndex}
                    priceInventory={priceInventory}
                    selectedSize={selectedSize}
                    baseStore={baseStore}
                    productSizeChart={sizeChart}
                    isQuickView={isQuickView}
                    productMetals={metals}
                    sizeSelectionError={sizeSelectionError}
                    isGiftCard={isGiftCard}
                    selectedAmount={selectedAmount}
                    cartMetaData={cartMetaData}
                    enagraveableProductConfig={enagraveableProductConfig}
                    metal={metal}
                    design={design}
                    text={text}
                    font={font}
                    icon={icon}
                    motifCode={motifCode}
                    fontCode={fontCode}
                    messageCharCount={messageCharCount}
                    displayAddtoBag
                    isPDPPage
                    isStackerPage={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Step4Confirm.propTypes = {
  design: PropTypes.string,
  font: PropTypes.string,
  text: PropTypes.string,
  icon: PropTypes.string,
  isQuickView: PropTypes.bool,
  motifCode: PropTypes.string,
  fontCode: PropTypes.string,
  setContainerHeight: PropTypes.func.isRequired,
  containerNode: PropTypes.node,
  setScroll: PropTypes.func,
  theme: PropTypes.string.isRequired,
  isPriceDisclaimerEnabled: PropTypes.bool.isRequired,
  configProductSet: PropTypes.array,
  isAffirmEnabledOnSite: PropTypes.bool,
  configTypeValue: PropTypes.string,
  metal: PropTypes.string,
}

export default Step4Confirm
